import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../utils/UserContext';
import IntegrationSettings from './IntegrationSettings';
import SettingsMenu, { SettingsTab } from './SettingsMenu';
import LinkedAccounts from './LinkedAccounts';
import AccountSettings from './AccountSettings';
import { isPreActiveCustomer } from '../home/utils/demoData';
import { CustomerWithDriverOnboarding } from '../../../../../types/apiTypes';

interface CustomerSettingsProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  currSubPage: string | null;
  setCurrSubPage: (page: string | null) => void;
}

function CustomerSettings({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  currSubPage,
  setCurrSubPage,
}: CustomerSettingsProps) {
  const { screenView } = useScreenView();
  const location = useLocation();
  const { userRecord } = useUser();

  const [selectedSettingsTab, setSelectedSettingsTab] = useState<SettingsTab>(
    SettingsTab.Account
  );

  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  useEffect(() => {
    switch (location.pathname) {
      case '/dashboard/settings/integrations':
        setCurrSubPage('integrations');
        setSelectedSettingsTab(SettingsTab.Integrations);
        break;
      case '/dashboard/settings/linked-accounts':
        setCurrSubPage('linked-accounts');
        setSelectedSettingsTab(SettingsTab.LinkedAccounts);
        break;
      case '/dashboard/settings/account':
        setCurrSubPage('account');
        setSelectedSettingsTab(SettingsTab.Account);
        break;
      default:
        setCurrSubPage(null);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, currentTab, userRecord]);

  const handleSetSettingsTab = (tab: SettingsTab) => {
    setSelectedSettingsTab(tab);
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div className="w-[1148px] max-w-[1148px] py-[16px]">
            <SettingsMenu
              setSelectedSettingsTab={handleSetSettingsTab}
              selectedSettingsTab={selectedSettingsTab}
            />
            {renderSubPage()}
          </div>
        );
      case 'tablet':
        return (
          <div className="h-full w-full min-w-[568px] overflow-y-auto overflow-x-hidden px-[16px] py-[16px]">
            <SettingsMenu
              setSelectedSettingsTab={handleSetSettingsTab}
              selectedSettingsTab={selectedSettingsTab}
            />
            {renderSubPage()}
          </div>
        );
      case 'mobile':
        return (
          <div className="h-full w-full min-w-[343px] overflow-y-auto overflow-x-hidden px-[16px] py-[16px]">
            <SettingsMenu
              setSelectedSettingsTab={handleSetSettingsTab}
              selectedSettingsTab={selectedSettingsTab}
            />
            {renderSubPage()}
          </div>
        );
    }
  };

  const renderSubPage = () => {
    switch (location.pathname) {
      case '/dashboard/settings/integrations':
        return (
          <div className="h-full w-full justify-center overflow-y-auto overflow-x-hidden">
            <div className="flex h-full w-full flex-col justify-center space-y-[16px] py-[16px]">
              <IntegrationSettings
                userType={userType}
                currentPage={currentPage}
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                subTab={subTab}
                setSubTab={setSubTab}
                isPreActive={isPreActive}
              />
            </div>
          </div>
        );
      case '/dashboard/settings/linked-accounts':
        return (
          <div className="h-full w-full justify-center overflow-y-auto overflow-x-hidden">
            <div className="flex h-full w-full flex-col justify-center space-y-[16px] py-[16px]">
              <LinkedAccounts
                userType={userType}
                currentPage={currentPage}
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                subTab={subTab}
                setSubTab={setSubTab}
                isPreActive={isPreActive}
              />
            </div>
          </div>
        );
      case '/dashboard/settings/account':
        return (
          <div className="h-full w-full justify-center overflow-y-auto overflow-x-hidden">
            <div className="flex h-full w-full flex-col justify-center space-y-[16px] py-[16px]">
              <AccountSettings
                userType={userType}
                currentPage={currentPage}
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                subTab={subTab}
                setSubTab={setSubTab}
                isPreActive={isPreActive}
              />
            </div>
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default CustomerSettings;
